import {createContext, ReactNode, useContext, useState} from "react"
import {styled} from "@mui/material/styles"
import {Snackbar, SnackbarContent} from "@mui/material"
import {paletteColors} from "@indebted/theme"

type NotificationState = {
	open: boolean
	duration: number
	severity: "success" | "error"
	message: string
}

const Ctx = createContext<{
	notification: {
		success: (message: string, duration?: number) => void
		error: (message: string, duration?: number) => void
	}
} | null>(null)
const useNotification = () => {
	const context = useContext(Ctx)
	if (!context) {
		throw new Error("useNotification must be used within a NotificationProvider")
	}
	return context
}

function NotificationProvider({children}: {children: ReactNode}) {
	const defaultState: NotificationState = {
		open: false,
		duration: 2500,
		severity: "success",
		message: "",
	}

	const [state, setState] = useState(defaultState)

	const notification = {
		success: (message: string, duration = defaultState.duration) => {
			setState({open: true, duration, severity: "success", message})
		},
		error: (message: string, duration = defaultState.duration) => {
			setState({open: true, duration, severity: "error", message})
		},
	}

	const handleClose = () => {
		setState(defaultState)
	}

	const origin = {
		vertical: "top",
		horizontal: "center",
	} as const

	return (
		<Ctx.Provider value={{notification}}>
			{children}
			<Snackbar anchorOrigin={origin} open={state.open} autoHideDuration={state.duration} onClose={handleClose}>
				<StyledSnackbarContent message={state.message} severity={state.severity} />
			</Snackbar>
		</Ctx.Provider>
	)
}

const colorMap = {
	success: paletteColors.onlineGreen,
	error: paletteColors.errorRed,
} as const

const StyledSnackbarContent = styled(SnackbarContent)<{severity: NotificationState["severity"]}>`
	background: ${({severity}) => colorMap[severity]};
`

export {NotificationProvider, useNotification}
